/**
 * Shared tidal-curve maths, reusable across every height source that yields HW/LW
 * events (Admiralty, FES, RYA training). Between two official events we fill the
 * height with a cosine half-cycle: it sits at the event height at each end (slack
 * water at HW and LW) and curves smoothly between, which closely matches the
 * standard Admiralty tidal-curve / rule-of-twelfths shape for a semidiurnal tide.
 * Honest about what it is: an interpolation between the official HW/LW, not the
 * port's published curve (which the free Admiralty tier doesn't provide).
 */
export interface TideMarker {
  t: number;
  heightM: number;
  type: 'H' | 'L';
}

/** Height at `ms` from sorted {t,heightM} events, cosine-filled between them. */
export function eventsHeightAt(evs: { t: number; heightM: number }[], ms: number): number {
  if (evs.length === 0) return 0;
  if (evs.length === 1) return evs[0].heightM;
  let a = evs[0];
  let b = evs[1];
  for (let i = 0; i + 1 < evs.length; i++) {
    if (evs[i].t <= ms && ms <= evs[i + 1].t) {
      a = evs[i];
      b = evs[i + 1];
      break;
    }
    if (ms > evs[i + 1].t) {
      a = evs[evs.length - 2];
      b = evs[evs.length - 1];
    }
  }
  const span = b.t - a.t;
  const frac = span > 0 ? Math.min(1, Math.max(0, (ms - a.t) / span)) : 0;
  return a.heightM + ((b.heightM - a.heightM) * (1 - Math.cos(Math.PI * frac))) / 2;
}

/** The UTC calendar day [00:00, 24:00) containing `ms`, for the day's curve. */
export function utcDayWindow(ms: number): { start: number; end: number } {
  const d = new Date(ms);
  const start = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0);
  return { start, end: start + 24 * 3_600_000 };
}

/** Sample a height function across [start,end] at `stepMs`, for plotting. */
export function sampleCurve(
  heightAt: (ms: number) => number,
  start: number,
  end: number,
  stepMs = 10 * 60_000,
): { t: number; h: number }[] {
  const out: { t: number; h: number }[] = [];
  for (let t = start; t <= end; t += stepMs) out.push({ t, h: heightAt(t) });
  return out;
}

/**
 * Find the HW/LW turning points of a height function across a window, so any
 * source (Admiralty events, FES predictor, training curve) yields the same
 * marker set. Dedupes points closer than ~2.5 h, keeping the more extreme.
 */
export function findExtrema(
  heightAt: (ms: number) => number,
  start: number,
  end: number,
  stepMs = 5 * 60_000,
): TideMarker[] {
  const raw: TideMarker[] = [];
  let pPrev = heightAt(start);
  let p = heightAt(start + stepMs);
  for (let t = start + stepMs; t + stepMs <= end; t += stepMs) {
    const n = heightAt(t + stepMs);
    if (Number.isFinite(p) && Number.isFinite(pPrev) && Number.isFinite(n)) {
      // Plateau-tolerant: a turning point at the end of a flat (the cosine fill is
      // flat at slack water) still counts, as long as one side is strictly beyond.
      if (p >= pPrev && p >= n && (p > pPrev || p > n)) raw.push({ t, heightM: p, type: 'H' });
      else if (p <= pPrev && p <= n && (p < pPrev || p < n)) raw.push({ t, heightM: p, type: 'L' });
    }
    pPrev = p;
    p = n;
  }
  // Dedupe near-duplicates (keep the more extreme of a close cluster).
  const out: TideMarker[] = [];
  for (const m of raw) {
    const last = out[out.length - 1];
    if (last && Math.abs(m.t - last.t) < 2.5 * 3_600_000 && last.type === m.type) {
      const moreExtreme = m.type === 'H' ? m.heightM > last.heightM : m.heightM < last.heightM;
      if (moreExtreme) out[out.length - 1] = m;
    } else {
      out.push(m);
    }
  }
  return out;
}

/**
 * The calendar day [00:00, 24:00) containing `ms` IN THE PORT'S OWN TIME ZONE,
 * returned as the real UTC-ms window that day spans. `tzOffsetMs` is the port's
 * offset from UT in milliseconds (e.g. −1 h = −3_600_000). With tzOffsetMs 0 this
 * is exactly utcDayWindow; with a southern training port (−1 h) it slides the
 * window so the curve, the HW/LW list and the readouts all sit on the same
 * port-local day the almanac and the RYA courses use.
 */
export function localDayWindow(ms: number, tzOffsetMs = 0): { start: number; end: number } {
  const local = new Date(ms + tzOffsetMs);
  const startLocal = Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate(), 0, 0, 0);
  const start = startLocal - tzOffsetMs;
  return { start, end: start + 24 * 3_600_000 };
}
